import { defineStore } from "pinia";
import type { PlotMarker } from "@/types/plot_marker";
import { usePwaStore } from "@/stores/pwa";
import { useAuthStore } from "@/stores/auth";
import { useAlertStore } from "@/stores/alert";
import axios from "@/plugins/axios";
import { v4 as uuidv4 } from "uuid";

/**
 * A store module for managing the PlotMarker resource.
 *
 * In the following, when interacting with the API, errors are handled by:
 * 1. setting requireSync to true to recover via syncing
 * 2. rethrow the error for console output and for BugSnag
 */
export const usePlotMarkerStore = defineStore("plot_marker", () => {
	/**
	 * Store a plot marker by calling the API plot marker store endpoint.
	 */
	const store = async function (plot_marker: PlotMarker): Promise<void> {
		const authStore = useAuthStore();
		const pwaStore = usePwaStore();
		const now = new Date();

		// Update the current user store
		for (const client of authStore.user.clients) {
			for (const estate of client.estates) {
				const site = estate.sites.find((x: any) => x.id == plot_marker.site_id);
				if (site) {
					site.plot_markers.push(plot_marker);
					break;
				}
			}
		}

		// Try to persist it
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.post(route("api.plot_marker.store"), plot_marker).then((response)=>{
				const plotMarkerReturned = response.data.data;
				// gets the synced_at version to match the DB, and adopts the
				// server's created_at/updated_at (it may overwrite them on save)
				plot_marker.synced_at = plotMarkerReturned.synced_at;
				plot_marker.created_at = plotMarkerReturned.created_at;
				plot_marker.updated_at = plotMarkerReturned.updated_at;
			}).catch(error=>{
				pwaStore.requireSync = true;
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}
	};

	/**
	 * Update a plot_marker by calling the API plot_marker update endpoint.
	 */
	const update = async function (plot_marker: PlotMarker): Promise<void> {
		const pwaStore = usePwaStore();
		const alertStore = useAlertStore();
		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		plot_marker.updated_at = isoString;

		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.patch(route("api.plot_marker.update", { id: plot_marker.id }), plot_marker)
				.then((response) => {
					const plotMarkerReturned = response.data.data;
					if (response.data.code == 422) {
						console.warn("Data in the database is newer than this Plot Marker. Could not overwrite. Data has now been synced for this Plot Marker but we recommend manually syncing your account. (Click on your name, then 'manually sync').");
						alertStore.info("Marker data in the database is newer than this location. Data has now been synced for this marker. Please check that your recent change has been saved. We recommend manually syncing your account. (Click on your name, then 'manually sync').");
						updatePlotMarkerInStore(plotMarkerReturned);
						return;
					}
					// assuming there hasnt been a real error then we want to reset to
					// the version returned by the attempted sync:
					plot_marker.synced_at = plotMarkerReturned.synced_at;
					plot_marker.updated_at = plotMarkerReturned.updated_at;
				})
				.catch(error=>{
					pwaStore.requireSync = true;
					throw error;
				});
		}
		else {
			pwaStore.requireSync = true;
		}
	};

	/**
	 * after a rejected (conflicting) update, replace the store's copy of this
	 * plot_marker with the authoritative version returned by the server
	 */
	const updatePlotMarkerInStore = function (plot_marker: PlotMarker): void {
		const authStore = useAuthStore();
		for (const client of authStore.user.clients) {
			for (const estate of client.estates) {
				for (const site of estate.sites) {
					if (site.plot_markers) for (let i = 0; i < site.plot_markers.length; i++) {
						if (site.plot_markers[i].id == plot_marker.id) {
							Object.assign(site.plot_markers[i], plot_marker);
						}
					}
				}
			}
		}
	};

	/**
	 * Destroy a plot_marker by calling the API plot_marker destroy endpoint.
	 */
	const destroy = async function (plot_marker: PlotMarker): Promise<void> {
		const authStore = useAuthStore();
		const pwaStore = usePwaStore();

		// Mark the plot_marker as deleted
		const now = new Date();
		const isoString = now.toISOString();//.replace("T", " ");
		plot_marker.deleted_at = isoString;
		plot_marker.updated_at = isoString;

		// Try to persist it
		if (pwaStore.onlineAndConnected && pwaStore.manuallyOnline) {
			await axios.delete(route("api.plot_marker.destroy", { id: plot_marker.id })).then(() => {
				// console.log("deleted plot_marker");
				// Remove local soft-deleted plot_markers by refetching data
				authStore.getCurrentUser();
			}).catch(error=>{
				pwaStore.requireSync = true;
				throw error;
			});
		}
		else {
			pwaStore.requireSync = true;
		}
	};

	return { store, update, destroy };
}, {
	persist: {
		// Ensure none of this store is persisted. Clients are persisted via the
		// current user store.
		paths: []
	}
});
